/**
 * Client-side Cache Utility
 * Provides browser-based caching for Instagram feed data
 */

/**
 * Cache class using localStorage
 */
export class Cache {
  /**
   * Creates a cache instance
   * @param {string} prefix - Cache key prefix
   * @param {number} ttl - Time to live in milliseconds
   */
  constructor(prefix = 'instagram_widget', ttl = 14400000) { // 4 hours default
    this.prefix = prefix;
    this.ttl = ttl;
    this.storage = this.getStorage();
  }

  /**
   * Gets available storage mechanism (localStorage or memory fallback)
   * @returns {Object} Storage object
   */
  getStorage() {
    try {
      // Test if localStorage is available
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return localStorage;
    } catch (e) {
      // Fallback to memory storage if localStorage is not available
      console.warn('localStorage not available, using memory cache');
      return this.createMemoryStorage();
    }
  }

  /**
   * Creates an in-memory storage fallback
   * @returns {Object} Memory storage object
   */
  createMemoryStorage() {
    const storage = {};
    return {
      getItem: (key) => storage[key] || null,
      setItem: (key, value) => { storage[key] = value; },
      removeItem: (key) => { delete storage[key]; },
      clear: () => { Object.keys(storage).forEach(key => delete storage[key]); }
    };
  }

  /**
   * Generates cache key
   * @param {string} key - Base key
   * @returns {string} Prefixed cache key
   */
  getCacheKey(key) {
    return `${this.prefix}_${key}`;
  }

  /**
   * Gets cached data
   * @param {string} key - Cache key
   * @returns {*} Cached data or null
   */
  get(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cached = this.storage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const parsed = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is expired
      if (now > parsed.expiry) {
        this.remove(key);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  }

  /**
   * Sets cached data
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {number} ttl - Optional custom TTL in milliseconds
   * @returns {boolean} Success status
   */
  set(key, data, ttl = null) {
    try {
      const cacheKey = this.getCacheKey(key);
      const expiry = Date.now() + (ttl || this.ttl);

      const cacheData = {
        data,
        expiry,
        timestamp: Date.now()
      };

      this.storage.setItem(cacheKey, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('Cache set error:', error);

      // If quota exceeded, try to clear old cache
      if (error.name === 'QuotaExceededError') {
        this.clear();
        try {
          const cacheKey = this.getCacheKey(key);
          const expiry = Date.now() + (ttl || this.ttl);
          const cacheData = { data, expiry, timestamp: Date.now() };
          this.storage.setItem(cacheKey, JSON.stringify(cacheData));
          return true;
        } catch (retryError) {
          console.error('Cache set failed after clearing:', retryError);
        }
      }

      return false;
    }
  }

  /**
   * Removes cached data
   * @param {string} key - Cache key
   */
  remove(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      this.storage.removeItem(cacheKey);
    } catch (error) {
      console.error('Cache remove error:', error);
    }
  }

  /**
   * Clears all cache with matching prefix
   */
  clear() {
    try {
      const keys = Object.keys(this.storage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          this.storage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Cache clear error:', error);
    }
  }

  /**
   * Checks if cache exists and is valid
   * @param {string} key - Cache key
   * @returns {boolean} True if valid cache exists
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Gets cache age in milliseconds
   * @param {string} key - Cache key
   * @returns {number|null} Age in milliseconds or null if not cached
   */
  getAge(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cached = this.storage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const parsed = JSON.parse(cached);
      return Date.now() - parsed.timestamp;
    } catch (error) {
      return null;
    }
  }
}

export default Cache;
